import React, { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

export default function RoiCalculatorWidget() {
  const { t } = useTranslation();

  const [aov, setAov] = useState(45);
  const [orders, setOrders] = useState(350);
  const [mkpFee, setMkpFee] = useState(40);

  // NUEVO: cantidad de locales
  const [locations, setLocations] = useState(1);

  const fmt = useMemo(
    () =>
      new Intl.NumberFormat("es-PA", {
        style: "currency",
        currency: "USD",
      }),
    []
  );

  const revenue = aov * orders;

  // costo marketplace (%)
  const mkpCost = revenue * (mkpFee / 100);

  // NUEVO: costo fijo
  // 1 local = 129
  // locales adicionales = +79
  const ourCost =
    locations <= 1
      ? 129
      : 129 + (locations - 1) * 79;

  const savings = Math.max(0, mkpCost - ourCost);

  const savingsPct =
    mkpCost > 0 ? (savings / mkpCost) * 100 : 0;

  const roi =
    ourCost > 0 ? (savings / ourCost) * 100 : 0;

  const maxCost = Math.max(mkpCost, ourCost, 1);

  const reset = () => {
    setAov(45);
    setOrders(350);
    setMkpFee(12);
    setLocations(1);
  };

  return (
    <div className="w-full max-w-screen-2xl rounded-3xl mx-auto p-4 md:p-6 text-gray-900 bg-gray-50">

      {/* HEADER */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">
            {t("roi_title")}
          </h1>

          <p className="text-gray-600 text-sm md:text-base mt-1">
            {t("roi_subtitle")}
          </p>
        </div>

        <button
          onClick={reset}
          className="inline-flex items-center gap-2 rounded-xl border border-gray-300 px-3 py-2 text-sm hover:bg-gray-100 active:scale-[.98] transition"
        >
          <RefreshIcon className="w-4 h-4" />
          {t("roi_reset")}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* INPUTS */}
        <div className="rounded-2xl bg-white border border-gray-200 p-6 shadow-sm">
          <h2 className="text-lg mb-6">
            {t("roi_section_inputs")}
          </h2>

          <div className="space-y-6">

            <SliderField
              label="Precio promedio por orden (AOV)"
              value={aov}
              onChange={setAov}
              min={1}
              max={200}
              step={1}
              suffix="$"
            />

            <SliderField
              label="Órdenes al mes"
              value={orders}
              onChange={(v) => setOrders(Math.floor(v))}
              min={0}
              max={2000}
              step={10}
            />

            <SliderField
              label="% comisión marketplace"
              value={mkpFee}
              onChange={setMkpFee}
              min={0}
              max={40}
              step={0.5}
              suffix="%"
            />

            {/* NUEVO */}
            <SliderField
              label="Cantidad de locales"
              value={locations}
              onChange={(v) => setLocations(Math.floor(v))}
              min={1}
              max={20}
              step={1}
            />
          </div>
        </div>

        {/* RESULTADOS */}
        <div>

          {/* KPI PRINCIPAL */}
          <div className="rounded-2xl bg-gradient-to-br from-indigo-600 to-indigo-500 text-white p-6 shadow-lg">

            <div className="mb-6">
              <div className="text-sm opacity-80">
                {t("roi_kpi_savings")}
              </div>

              <div className="text-4xl md:text-5xl font-bold tracking-tight">
                {fmt.format(savings)}
              </div>

              <div className="text-sm mt-1 opacity-80">
                {savingsPct.toFixed(1)}% vs marketplace
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">

              <div className="bg-white/10 rounded-xl p-4 backdrop-blur">
                <div className="text-xs opacity-70">
                  ROI
                </div>

                <div className="text-2xl font-semibold">
                  {roi.toFixed(1)}%
                </div>
              </div>

              <div className="bg-white/10 rounded-xl p-4 backdrop-blur">
                <div className="text-xs opacity-70">
                  Ingresos
                </div>

                <div className="text-2xl font-semibold">
                  {fmt.format(revenue)}
                </div>
              </div>
            </div>
          </div>

          {/* COSTOS */}
          <div className="grid grid-cols-2 gap-4 mt-4">

            <div className="rounded-xl border p-4 bg-white shadow-sm">
              <div className="text-xs text-gray-500">
                {t("roi_kpi_mkp_cost")}
              </div>

              <div className="text-3xl font-semibold text-red-500">
                {fmt.format(mkpCost)}
              </div>
            </div>

            <div className="rounded-xl border p-4 bg-white shadow-sm">
              <div className="text-xs text-gray-500">
                Costo mensual con nosotros
              </div>

              <div className="text-3xl font-semibold text-green-600">
                {fmt.format(ourCost)}
              </div>
            </div>
          </div>

          {/* BARRAS */}
          <div className="mt-6">

            <div className="text-xs text-gray-500 mb-2">
              {t("roi_cost_comparison")}
            </div>

            <div className="space-y-3">

              <BarRow
                label={t("roi_marketplace")}
                value={mkpCost}
                max={maxCost}
                fmt={fmt}
                tone="bg-gradient-to-r from-red-400 to-red-500"
              />

              <BarRow
                label={t("roi_with_us")}
                value={ourCost}
                max={maxCost}
                fmt={fmt}
                tone="bg-gradient-to-r from-green-400 to-green-500"
                highlight={ourCost < mkpCost}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- COMPONENTES ---------- */

function SliderField({
  label,
  value,
  onChange,
  min,
  max,
  step,
  suffix = "",
}) {
  const percentage =
    ((value - min) / (max - min)) * 100;

  return (
    <div className="space-y-3">

      {/* LABEL + VALUE */}
      <div className="flex items-center justify-between">

        <span className="text-sm text-gray-500">
          {label}
        </span>

        <div className="px-3 py-1 rounded-full bg-indigo-50 text-indigo-600 text-sm font-semibold tabular-nums">
          {value}{suffix}
        </div>
      </div>

      {/* SLIDER */}
      <div className="relative w-full">

        {/* TRACK BACKGROUND */}
        <div className="absolute top-1/2 -translate-y-1/2 w-full h-2 rounded-full bg-gray-200" />

        {/* TRACK ACTIVE */}
        <div
          className="absolute top-1/2 -translate-y-1/2 h-2 rounded-full bg-gradient-to-r from-indigo-500 to-indigo-400 transition-all"
          style={{ width: `${percentage}%` }}
        />

        {/* INPUT RANGE */}
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) =>
            onChange(Number(e.target.value))
          }
          className="relative w-full appearance-none bg-transparent z-10"
        />

        {/* THUMB STYLES */}
        <style>{`
          input[type=range]::-webkit-slider-thumb {
            appearance: none;
            width: 18px;
            height: 18px;
            border-radius: 999px;
            background: white;
            border: 3px solid #6366f1;
            box-shadow: 0 4px 10px rgba(0,0,0,0.15);
            cursor: pointer;
            transition: transform 0.15s ease;
          }

          input[type=range]::-webkit-slider-thumb:hover {
            transform: scale(1.15);
          }

          input[type=range]::-moz-range-thumb {
            width: 18px;
            height: 18px;
            border-radius: 999px;
            background: white;
            border: 3px solid #6366f1;
            box-shadow: 0 4px 10px rgba(0,0,0,0.15);
            cursor: pointer;
          }

          input[type=range]::-webkit-slider-runnable-track {
            background: transparent;
          }

          input[type=range]::-moz-range-track {
            background: transparent;
          }
        `}</style>
      </div>

      {/* MIN / MAX */}
      <div className="flex justify-between text-[11px] text-gray-400">
        <span>{min}</span>
        <span>{max}</span>
      </div>
    </div>
  );
}

function BarRow({
  label,
  value,
  max,
  fmt,
  tone,
  highlight,
}) {
  const [width, setWidth] = useState(0);

  const targetWidth = Math.max(
    4,
    Math.round((value / max) * 100)
  );

  useEffect(() => {
    const timeout = setTimeout(() => {
      setWidth(targetWidth);
    }, 100);

    return () => clearTimeout(timeout);
  }, [targetWidth]);

  return (
    <div className="w-full">

      {/* LABEL */}
      <div className="flex items-center justify-between text-xs text-gray-500 mb-1">

        <span>{label}</span>

        <span className="font-medium">
          {fmt.format(value)}
        </span>
      </div>

      {/* BAR */}
      <div className="relative w-full h-4 rounded-full bg-gray-200 overflow-hidden">

        {/* animated bar */}
        <div
          className={`
            h-full rounded-full transition-all duration-900 ease-out
            ${tone}
            ${highlight ? "shadow-lg shadow-green-400 animate-pulse" : ""}
          `}
          style={{ width: width + "%" }}
        />

        {/* shine */}
        <div
          className="absolute top-0 left-0 h-full w-full opacity-20 pointer-events-none"
          style={{
            background:
              "linear-gradient(120deg, transparent 30%, rgba(255,255,255,0.6), transparent 70%)",
            animation: "shine 2.5s infinite",
          }}
        />
      </div>

      <style>{`
        @keyframes shine {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
    </div>
  );
}

function RefreshIcon({ className }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className={className}
    >
      <path d="M20 12a8 8 0 10-3 6.32" />
      <path d="M20 8v4h-4" />
    </svg>
  );
}